import { SheetWithFormComponent } from '@/components/ui/SheetWithFormComponent'
import { useRestClient, RestSchemas } from '@/contexts/QueryProvider'
import { zodResolver } from '@hookform/resolvers/zod'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { FormComponentLoading } from '@/catalog-apps/components/component-library/FormComponent'

import { Button } from '@/components/shadcn/button'
import { Form } from '@/components/shadcn/form'
import { useCurrentOrganisation } from '../useCurrentOrganisation'
import { ErrorComponent } from '@/ui-legacy/ErrorComponent'
import {
  useWorkspaceFormOptions,
  WorkspaceApplicationTeamsFields,
} from './components/WorkspaceApplicationTeamsFields'

const formSchema = z.object({
  projectId: z.number().min(1, 'Project is required'),
  applicationId: z.number().min(1, 'Application is required'),
  projectTeamIds: z.array(z.number()).default([]),
  supportTeamIds: z.array(z.number()).default([]),
})

export type TCreateWorkspaceForm = z.infer<typeof formSchema>

type MessagesSubscriptionsNewOrganisationWorkspace = RestSchemas['Messages.Subscriptions.NewOrganisationWorkspace']
type MessagesSubscriptionsNewOrganisationWorkspaceTeam =
  RestSchemas['Messages.Subscriptions.NewOrganisationWorkspaceTeam']

type CreateWorkspaceFormProps = {
  onSubmit?: () => void
  sheetTrigger?: React.ReactNode
  sheetTriggerTooltip?: string
  sheetTitle?: string
  sheetDescription?: string
  openInSheet?: boolean
  organisationId?: number
  projectId?: number
  excludedAppIds?: number[]
}

type WorkspacePromiseResult = {
  workspaceId?: number
  message?: string
}

export const CreateWorkspaceForm = (props: CreateWorkspaceFormProps) => {
  const [sheetOpen, setSheetOpen] = useState(false)

  const { onSubmit: onSubmitProp, ...rest } = props
  const onSubmit = useCallback(() => {
    onSubmitProp?.()
    setSheetOpen(false)
  }, [onSubmitProp])

  return props.sheetTrigger || props.openInSheet ? (
    <SheetWithFormComponent
      title={props.sheetTitle}
      description={props.sheetDescription}
      trigger={props.sheetTrigger}
      triggerTooltip={props.sheetTriggerTooltip}
      wide
      isOpen={sheetOpen}
      onOpenChange={setSheetOpen}
    >
      <FormComponent {...rest} onSubmit={onSubmit} />
    </SheetWithFormComponent>
  ) : (
    <FormComponent {...rest} onSubmit={onSubmit} />
  )
}

const FormComponent = (props: CreateWorkspaceFormProps) => {
  const { onSubmit, projectId: lockedProjectId, excludedAppIds = [] } = props

  const { id: orgId } = useCurrentOrganisation(props.organisationId)

  const {
    getSubscriptionsApiGetOrganisationTeams,
    putSubscriptionsApiAddOrganisationWorkspaces,
    putWorkspacesApiAddProjectTeamToWorkspace,
    putWorkspacesApiAddSupportTeamToWorkspace,
    getSubscriptionsApiGetOrganisations,
    getSubscriptionsApiGetOrganisationWorkspacesSummary,
    getProjectsApiGetUserLinkedProjects,
  } = useRestClient()

  const { data: projectTeams } = getSubscriptionsApiGetOrganisationTeams.useQuery(
    { query: { orgId: orgId! } },
    { enabled: !!orgId }
  )

  const { mutate: addOrganisationWorkspace, isPending: isAddingWorkspace } =
    putSubscriptionsApiAddOrganisationWorkspaces.useMutation()
  const { mutateAsync: addProjectTeamToWorkspace } = putWorkspacesApiAddProjectTeamToWorkspace.useMutation()
  const { mutateAsync: addSupportTeamToWorkspace } = putWorkspacesApiAddSupportTeamToWorkspace.useMutation()

  const defaultValues = useMemo(
    () => ({
      projectId: lockedProjectId ?? 0,
      applicationId: 0,
      projectTeamIds: [],
      supportTeamIds: [],
    }),
    [lockedProjectId]
  )

  const form = useForm<TCreateWorkspaceForm>({
    resolver: zodResolver(formSchema),
    defaultValues,
  })

  useEffect(() => {
    if (lockedProjectId != null) {
      form.setValue('projectId', lockedProjectId)
    }
  }, [form, lockedProjectId])

  const selectedAppId = form.watch('applicationId')

  const { loading, error, supportTeamOptions } = useWorkspaceFormOptions(
    orgId,
    lockedProjectId,
    selectedAppId,
    excludedAppIds
  )

  const invalidateQueries = useCallback(() => {
    getSubscriptionsApiGetOrganisations.invalidateQueries({})
    getSubscriptionsApiGetOrganisationWorkspacesSummary.invalidateQueries({ parameters: { query: { orgId } } })
    getProjectsApiGetUserLinkedProjects.invalidateQueries({})
  }, [
    getProjectsApiGetUserLinkedProjects,
    getSubscriptionsApiGetOrganisations,
    getSubscriptionsApiGetOrganisationWorkspacesSummary,
    orgId,
  ])

  const onMutationSuccess = useCallback(() => {
    toast.success('Workspace successfully created')
    onSubmit?.()
    form.reset()
    invalidateQueries()
  }, [onSubmit, form, invalidateQueries])

  const handleSubmit = useCallback(
    async (data: TCreateWorkspaceForm) => {
      if (!orgId) {
        toast.error('No organisation ID found')
        return
      }

      const newWorkspace: MessagesSubscriptionsNewOrganisationWorkspace = {
        projectId: data.projectId,
        applicationId: data.applicationId,
      }

      addOrganisationWorkspace(
        { body: newWorkspace },
        {
          onSuccess: async ({ workspaceId }) => {
            const teamsToAddPromises: Promise<WorkspacePromiseResult>[] = []

            data.projectTeamIds.forEach((teamId) => {
              const team = projectTeams?.find((t) => t.id === teamId)
              if (team) {
                const newWorkspaceTeam: MessagesSubscriptionsNewOrganisationWorkspaceTeam = {
                  subscriptionId: workspaceId,
                  teamId: teamId,
                }

                const promise = addProjectTeamToWorkspace(
                  { body: newWorkspaceTeam },
                  {
                    onSuccess: () => {
                      toast.success(`Project Team ${team.name} successfully added`)
                    },
                  }
                )
                teamsToAddPromises.push(promise)
              }
            })

            data.supportTeamIds.forEach((teamId) => {
              const team = supportTeamOptions.find((t) => t.value === teamId)
              if (team) {
                const newWorkspaceTeam: MessagesSubscriptionsNewOrganisationWorkspaceTeam = {
                  subscriptionId: workspaceId,
                  teamId: teamId,
                }

                const promise = addSupportTeamToWorkspace(
                  { body: newWorkspaceTeam },
                  {
                    onSuccess: () => {
                      toast.success(`Support Team ${team.label} successfully added`)
                    },
                  }
                )
                teamsToAddPromises.push(promise)
              }
            })

            try {
              await Promise.all(teamsToAddPromises)
              onMutationSuccess()
            } catch (err) {
              toast.error('Error adding teams to workspace')
              console.error(err)
            }
          },
          onError: (error) => {
            console.error(error)
            toast.error('Failed to create workspace')
          },
        }
      )
    },
    [
      orgId,
      projectTeams,
      supportTeamOptions,
      addOrganisationWorkspace,
      addProjectTeamToWorkspace,
      addSupportTeamToWorkspace,
      onMutationSuccess,
    ]
  )

  if (loading) return <FormComponentLoading />
  if (error) return <ErrorComponent />

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <div className="flex h-full flex-col">
          <WorkspaceApplicationTeamsFields
            control={form.control}
            orgId={orgId}
            lockedProjectId={lockedProjectId}
            excludedAppIds={excludedAppIds}
          />
          <div className="form-action-container">
            <Button type="submit" disabled={isAddingWorkspace}>
              Create Workspace
            </Button>
          </div>
        </div>
      </form>
    </Form>
  )
}
